import { useCallback, useEffect, useState } from 'react'

import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import Skeleton from '@mui/material/Skeleton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Grid from '@mui/material/Unstable_Grid2'

import { useBoolean } from 'src/hooks/use-boolean'
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import { useSnackbar } from 'src/components/snackbar'
import {
  FinanceOperator,
  FinanceOperatorPayload,
  createOperator,
  deleteOperator,
  fetchOperators,
  updateOperator,
} from 'src/service/finance.service'

import { formatCurrency } from '../orders/format-currency'

const fmtPercent = (value: number) => `${(value || 0).toFixed(2).replace('.', ',')}%`

type FormState = {
  name: string
  credit: string
  debit: string
  pix: string
  fixedFee: string
  isDefault: boolean
  notes: string
}

const emptyForm: FormState = {
  name: '',
  credit: '',
  debit: '',
  pix: '',
  fixedFee: '',
  isDefault: false,
  notes: '',
}

const parseDecimal = (value: string) => {
  const parsed = Number(String(value).replace(',', '.'))
  return Number.isNaN(parsed) ? 0 : parsed
}

export default function OperatorsTab() {
  const { enqueueSnackbar } = useSnackbar()
  const isLoading = useBoolean(true)
  const isSaving = useBoolean(false)
  const [operators, setOperators] = useState<FinanceOperator[]>([])
  const [editing, setEditing] = useState<FinanceOperator | null>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      isLoading.onTrue()
      setOperators(await fetchOperators())
    } catch {
      setOperators([])
    } finally {
      isLoading.onFalse()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyForm, isDefault: operators.length === 0 })
  }

  const openEdit = (operator: FinanceOperator) => {
    setEditing(operator)
    setForm({
      name: operator.name,
      credit: String(operator.fees.credit ?? 0).replace('.', ','),
      debit: String(operator.fees.debit ?? 0).replace('.', ','),
      pix: String(operator.fees.pix ?? 0).replace('.', ','),
      fixedFee: operator.fixedFee ? String(operator.fixedFee / 100).replace('.', ',') : '',
      isDefault: operator.isDefault,
      notes: operator.notes || '',
    })
  }

  const handleSave = async () => {
    if (!form) return
    if (!form.name.trim()) {
      enqueueSnackbar('Informe o nome da operadora.', { variant: 'warning' })
      return
    }

    const payload: FinanceOperatorPayload = {
      name: form.name.trim(),
      fees: {
        credit: parseDecimal(form.credit),
        debit: parseDecimal(form.debit),
        pix: parseDecimal(form.pix),
      },
      // Valores monetários são armazenados em centavos.
      fixedFee: Math.round(parseDecimal(form.fixedFee) * 100),
      isDefault: form.isDefault,
      notes: form.notes,
    }

    try {
      isSaving.onTrue()
      if (editing) {
        await updateOperator(editing._id, payload)
        enqueueSnackbar('Operadora atualizada!')
      } else {
        await createOperator(payload)
        enqueueSnackbar('Operadora cadastrada!')
      }
      setForm(null)
      setEditing(null)
      await load()
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.error || 'Erro ao salvar operadora.', {
        variant: 'error',
      })
    } finally {
      isSaving.onFalse()
    }
  }

  const handleDelete = async (operator: FinanceOperator) => {
    try {
      setDeletingId(operator._id)
      await deleteOperator(operator._id)
      enqueueSnackbar('Operadora removida.')
      await load()
    } catch {
      enqueueSnackbar('Erro ao remover operadora.', { variant: 'error' })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <>
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
        >
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <Iconify icon='solar:card-bold' width={18} sx={{ color: 'primary.main' }} />
            <Typography variant='subtitle2' fontWeight={700}>
              Taxas de custo por operadora
            </Typography>
          </Stack>
          <Button
            size='small'
            variant='contained'
            startIcon={<Iconify icon='mingcute:add-line' width={16} />}
            onClick={openCreate}
          >
            Nova operadora
          </Button>
        </Stack>

        {isLoading.value ? (
          <Box sx={{ p: 2.5 }}>
            <Skeleton variant='rounded' height={200} />
          </Box>
        ) : (
          <TableContainer>
            <Scrollbar>
              <Table size='small' sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Operadora</TableCell>
                    <TableCell align='right'>Crédito</TableCell>
                    <TableCell align='right'>Débito</TableCell>
                    <TableCell align='right'>Pix</TableCell>
                    <TableCell align='right'>Fixo / transação</TableCell>
                    <TableCell align='center'>Padrão</TableCell>
                    <TableCell align='right' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operators.map((operator) => (
                    <TableRow key={operator._id} hover>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>
                          {operator.name}
                        </Typography>
                        {operator.notes && (
                          <Typography variant='caption' color='text.secondary'>
                            {operator.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align='right'>{fmtPercent(operator.fees.credit)}</TableCell>
                      <TableCell align='right'>{fmtPercent(operator.fees.debit)}</TableCell>
                      <TableCell align='right'>{fmtPercent(operator.fees.pix)}</TableCell>
                      <TableCell align='right'>
                        {operator.fixedFee ? formatCurrency(operator.fixedFee) : '—'}
                      </TableCell>
                      <TableCell align='center'>
                        {operator.isDefault && (
                          <Chip size='small' color='primary' variant='soft' label='Padrão' />
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Editar'>
                          <IconButton size='small' onClick={() => openEdit(operator)}>
                            <Iconify icon='solar:pen-bold' width={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Remover'>
                          <span>
                            <IconButton
                              size='small'
                              color='error'
                              disabled={deletingId === operator._id}
                              onClick={() => handleDelete(operator)}
                            >
                              <Iconify icon='solar:trash-bin-trash-bold' width={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!operators.length && (
                    <TableRow>
                      <TableCell colSpan={7} align='center' sx={{ py: 6 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Nenhuma operadora cadastrada. Cadastre a PagSeguro (ou outra) com as
                          taxas que ela cobra de você para calcular o DRE.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        )}
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{editing ? 'Editar operadora' : 'Nova operadora'}</DialogTitle>
        {form && (
          <DialogContent sx={{ pt: 1 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label='Nome (ex.: PagSeguro)'
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth
                size='small'
              />
              <Typography variant='caption' color='text.secondary'>
                Percentual de custo que a operadora cobra de você em cada meio de pagamento:
              </Typography>
              <Grid container spacing={1.5}>
                <Grid xs={4}>
                  <TextField
                    label='Crédito'
                    value={form.credit}
                    onChange={(e) => setForm({ ...form, credit: e.target.value })}
                    size='small'
                    fullWidth
                    InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                  />
                </Grid>
                <Grid xs={4}>
                  <TextField
                    label='Débito'
                    value={form.debit}
                    onChange={(e) => setForm({ ...form, debit: e.target.value })}
                    size='small'
                    fullWidth
                    InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                  />
                </Grid>
                <Grid xs={4}>
                  <TextField
                    label='Pix'
                    value={form.pix}
                    onChange={(e) => setForm({ ...form, pix: e.target.value })}
                    size='small'
                    fullWidth
                    InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                  />
                </Grid>
              </Grid>
              <TextField
                label='Custo fixo por transação (opcional)'
                value={form.fixedFee}
                onChange={(e) => setForm({ ...form, fixedFee: e.target.value })}
                size='small'
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position='start'>R$</InputAdornment> }}
                helperText='Cobrado em cada transação de crédito, débito ou pix.'
              />
              <TextField
                label='Observações'
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                size='small'
                fullWidth
                multiline
                minRows={2}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isDefault}
                    onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                  />
                }
                label='Operadora padrão (usada nos eventos sem operadora definida)'
              />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button color='inherit' onClick={() => setForm(null)}>
            Cancelar
          </Button>
          <Button variant='contained' onClick={handleSave} disabled={isSaving.value}>
            {isSaving.value ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
